// utils/toolNames.js
export const TOOL_NAME_MAP = {
  spend_analysis: "Spend Analytics Agent",
  market_intel: "Market Intel Agent",
  supplier_performance_risk_analyzer: "Supplier Risk Agent",
  benchmark: "Benchmarking Agent",
  supplier_intelligence: "Supplier Discovery Agent",
  supplier_discovery: "Supplier Discovery Agent"
  // add more mappings...
};

const TOOL_SLUG_LIST = Object.keys(TOOL_NAME_MAP).sort(
  (a, b) => b.length - a.length
);

// Home.jsx — add near other imports/constants
export const AGENT_COLORS = {
  "Orchestrator Agent": "#EC10A3",        // pink/magenta
  // "Orchestrator Agent": "#7C3AED",        // different darker purple for inner orchestrator agent working
  "Spend Analytics Agent": "#22D3EE",     // cyan
  "Market Intel Agent": "#F59E0B",        // orange
  "Supplier Risk Agent": "#EF4444",       // red
  "Supplier Discovery Agent": "#e31e5f",       // fuschia
  "Benchmarking Agent": "#10B981",    // green
  "Opportunity Identification Agent": "#8B5CF6",         // purple
  "Price Projection Agent": "#8B5CF6",         // purple
};

// export const detectAgentFromStatus = (status = "") => {
//   const s = status.toLowerCase();
//   if (s.includes("spend_analysis")) return "Spend Analytics Agent";
//   if (s.includes("market_intel")) return "Market Intel Agent";
//   if (s.includes("supplier_performance")) return "Supplier Risk Agent";
//   return null;
// };

export const detectAgentFromStatus = (status = "") => {
  const s = status.toLowerCase();
  
  // First check for RUNNING_NOW_ prefix anywhere in the text (including JSON)
  const runningMatch = s.match(/running_now_([a-z0-9_]+)/);
  if (runningMatch) {
    const toolKey = runningMatch[1]; // e.g., "spend_analysis"
    return TOOL_NAME_MAP[toolKey] || null;
  }
  
  // Also check in JSON "tool" field specifically
  try {
    // Look for "tool":"RUNNING_NOW_..." pattern in JSON
    const toolMatch = s.match(/"tool"\s*:\s*"running_now_([a-z0-9_]+)"/);
    if (toolMatch) {
      const toolKey = toolMatch[1];
      return TOOL_NAME_MAP[toolKey] || null;
    }
  } catch (e) {
    // Ignore JSON parsing errors
  }
  
  // Fallback to existing logic
  for (const [slug, display] of Object.entries(TOOL_NAME_MAP)) {
    if (s.includes(slug)) return display;
    if (display && s.includes(display.toLowerCase())) return display;
  }
  // if (s.includes("negotiation")) return "Opportunity Identification Agent";
  // if (s.includes("price_projection")) return "Price Projection Agent";
  
  return null;
};


export function getToolDisplayName(raw) {
  if (!raw) return "System";
  const key = String(raw).toLowerCase().trim();
  return TOOL_NAME_MAP[key] || raw;
}

function makeToolEventId() {
  return `tool_evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function extractToolSlug(raw = "") {
  const val = String(raw || "").trim();
  if (!val) return null;
  const runningMatch = val.match(/RUNNING_NOW_([a-z0-9_]+)/i);
  if (runningMatch && runningMatch[1]) {
    return runningMatch[1].toLowerCase();
  }
  return val.replace(/\s+completed$/i, "").toLowerCase();
}

function deriveAgentSlugFromToolSlug(toolSlug = "") {
  if (!toolSlug) return null;
  const normalized = toolSlug.toLowerCase();
  for (const slug of TOOL_SLUG_LIST) {
    if (normalized.startsWith(slug)) return slug;
  }
  return null;
}

export function extractTaskKeyFromStatus(text = "") {
  if (typeof text !== "string" || !text.trim()) return null;
  const directMatch = text.match(/RUNNING_NOW_([a-z0-9_]+)/i);
  if (directMatch && directMatch[1]) return directMatch[1].toLowerCase();
  const jsonMatch = text.match(/"tool"\s*:\s*"running_now_([a-z0-9_]+)"/i);
  if (jsonMatch && jsonMatch[1]) return jsonMatch[1].toLowerCase();
  return null;
}

function deriveStatus(rawStatus, toolVal = "") {
  if (typeof rawStatus === "string" && rawStatus.trim()) {
    return rawStatus.trim().toLowerCase();
  }
  if (/RUNNING_NOW_/i.test(toolVal) && !/completed/i.test(toolVal)) {
    return "running";
  }
  if (/completed/i.test(toolVal)) {
    return "completed";
  }
  return "unknown";
}

function normalizeFiles(files) {
  if (!Array.isArray(files)) return [];
  return files
    .map((file) => {
      const name = typeof file?.name === "string" ? file.name : "Download";
      const type =
        typeof file?.type === "string" ? file.type.toLowerCase() : "file";
      const url =
        typeof file?.url === "string" && file.url.trim() ? file.url : null;
      if (!url) return null;
      return { name, type, url };
    })
    .filter(Boolean);
}

function buildToolRegex(flags = "gi") {
  const keys = Object.keys(TOOL_NAME_MAP);
  return keys.length ? new RegExp(`\\b(${keys.join("|")})\\b`, flags) : null;
}

// Map raw tool tokens to friendly names, without changing the rest of the text.
export function mapToolTokensInText(text) {
  if (typeof text !== "string") return text;
  const rx = buildToolRegex();
  return rx ? text.replace(rx, (m) => getToolDisplayName(m)) : text;
}

// Normalize ONLY tool-name related phrases; no hardcoded status-word replacements.
export function normalizeStatusLine(line) {
  if (typeof line !== "string" || !line.trim()) return "";

  let out = line;

  // Map "<tool>" occurrences anywhere
  out = mapToolTokensInText(out);

  // Normalize common tool-prefixed patterns to keep names friendly
  const toolRx = buildToolRegex("i");
  if (toolRx) {
    // "Running <tool>…"
    out = out.replace(
      new RegExp(String.raw`^(RUNNING_NOW|Using)\s+(${toolRx.source})\b(.*)`, "i"),
      (_m, verb, tool, rest) => `${verb} ${getToolDisplayName(tool)}${rest || ""}`
    );

    // "<tool> executed"
    out = out.replace(
      new RegExp(String.raw`^(${toolRx.source})\s+executed\b`, "i"),
      (_m, tool) => `${getToolDisplayName(tool)} executed`
    );

    // "Response generated from <tool>"
    out = out.replace(
      new RegExp(String.raw`^Response generated from\s+(${toolRx.source})\b`, "i"),
      (_m, tool) => `Response generated from ${getToolDisplayName(tool)}`
    );
  }

  return out.trim();
}


// utils/tools.js  ✅ NEW
export function detectAgentFromLog(line = "") {
  if (!line || typeof line !== "string") return null;
  const lowered = line.toLowerCase();

  // Prefer explicit RUNNING_NOW_<slug>
  const m = lowered.match(/running_now_([a-z0-9_]+)/i);
  if (m && m[1]) {
    const raw = m[1]; // e.g. "spend_analysis"
    return TOOL_NAME_MAP[raw] || getToolDisplayName(raw);
  }

  // Fallback: if a known slug appears anywhere in the line
  for (const slug of Object.keys(TOOL_NAME_MAP)) {
    if (lowered.includes(slug)) return TOOL_NAME_MAP[slug];
  }
  return null;
}

// ----- log classification helpers -----
function looksLikeToolLine(text = "") {
  if (!text) return false;
  return /^(tool\s+\d+[:|-]|agent\s+running)/i.test(text.trim());
}

function normalizeStateFromText(text = "") {
  const lower = text.toLowerCase();
  if (/(completed|done|generated|success|finished)/i.test(lower)) return "completed";
  if (/(failed|error|unable|timeout)/i.test(lower)) return "error";
  if (/(running|preparing|starting|queued)/i.test(lower)) return "running";
  return "info";
}

const ORCHESTRATOR_KEYWORDS = [
  "orchestrator",
  // "summarising results",
  "navigating your data",
  "summarising results for you and generating artifacts",
  // "summarizing results",
  // "preparing plan",
  // "narrating outcome",
];

export function classifyLogEntry(text = "", { withinPlan = false } = {}) {
  const clean = (text ?? "").trim();
  if (!clean) return { kind: "text", text: "" };
  const lower = clean.toLowerCase();

  if (clean === "Plan") {
    return { kind: "plan-heading", text: clean };
  }

  const looksLikeStatus = ORCHESTRATOR_KEYWORDS.some((kw) => lower.includes(kw));
  if (looksLikeStatus) {
    return {
      kind: "status-pill",
      text: clean,
      meta: { state: normalizeStateFromText(clean), signalsCompletion: true },
    };
  }

  if (looksLikeToolLine(clean) || lower.includes("agent running")) {
    const agentName = detectAgentFromStatus(clean);
    const taskKey = extractTaskKeyFromStatus(clean);
    return {
      kind: "agent-status",
      text: clean,
      meta: {
        agentName,
        state: normalizeStateFromText(clean),
        isTool: /^tool/i.test(clean),
        taskKey,
      },
    };
  }

  if (withinPlan) {
    return {
      kind: "plan-paragraph",
      text: clean,
    };
  }

  return { kind: "text", text: clean };
}

// --- Artifact helpers ---
export const ARTIFACT_KEY_MAP = {
  image: [
    "artifact_image_url",
    "artifactImageUrl",
    "artifact_image",
    "image_url",
    "imageUrl",
    "chart_url",
    "chartUrl",
    "image",
    "chart",
  ],
  report: [
    "report_url",
    "reportUrl",
    "report",
    "pdf_url",
    "pdfUrl",
    "document_url",
    "documentUrl",
    "file_url",
    "fileUrl",
  ],
};

export const _firstString = (obj, keys) =>
  keys.map((k) => obj?.[k]).find((v) => typeof v === "string" && v.trim()) ||
  null;

/**
 * Accepts either a full payload (possibly with nested .artifacts/.data.artifacts)
 * or a raw artifacts object. Returns normalized keys.
 */
export function normalizeArtifacts(input) {
  const artifacts = input?.artifacts ?? input?.data?.artifacts ?? input ?? {};
  return {
    artifact_image_url: _firstString(artifacts, ARTIFACT_KEY_MAP.image),
    report_url: _firstString(artifacts, ARTIFACT_KEY_MAP.report),
  };
}

export function normalizeToolEvent(raw) {
  if (!raw) return null;
  const obj = parseMaybeJSON(raw) || raw;
  if (!obj || typeof obj !== "object") return null;

  const toolVal =
    (typeof obj.tool === "string" && obj.tool) ||
    (typeof obj?.data?.tool === "string" && obj.data.tool) ||
    extractFieldString(raw, "tool") ||
    "";

  const slug = extractToolSlug(toolVal);
  const agentSlug = deriveAgentSlugFromToolSlug(slug);
  const agentName =
    detectAgentFromStatus(toolVal) ||
    getToolDisplayName(agentSlug || slug || toolVal) ||
    "Agent";

  const data = obj?.data && typeof obj.data === "object" ? obj.data : {};
  const status = deriveStatus(data?.status, toolVal);

  const durationMs = Number(data?.duration_ms);
  const hasDuration = Number.isFinite(durationMs) && durationMs >= 0;
  const durationSeconds = hasDuration
    ? Math.max(1, Math.floor(durationMs / 1000))
    : null;
  const durationMinutes =
    durationSeconds != null
      ? Math.max(1, Math.floor(durationSeconds / 60))
      : null;

  const files = normalizeFiles(data?.files);
  const summary =
    typeof data?.summary === "string" ? data.summary.trim() : null;

  const timestamp = Date.now();

  return {
    id: makeToolEventId(),
    toolValue: toolVal || null,
    toolSlug: slug,
    agentSlug,
    agentName,
    status,
    durationMs: hasDuration ? durationMs : null,
    durationSeconds,
    durationMinutes,
    summary,
    files,
    raw: obj,
    timestamp,
  };
}

// Turn "spend_analysis" or "RUNNING_NOW_spend_analysis" into a nice title
function titleizeSlug(s) {
  return String(s || "")
    .replace(/^RUNNING_NOW_/i, "")
    .split("_")
    .map(w => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ")
    .trim();
}

function resolveToolName(raw = "") {
  const slug = String(raw || "").replace(/^RUNNING_NOW_/i, "");
  const mapped = TOOL_NAME_MAP[slug.toLowerCase()];
  if (mapped) return mapped;
  const titled = titleizeSlug(slug);
  return /agent$/i.test(titled) ? titled : `${titled} Agent`;
}

// Format a tool value into the final message
// "RUNNING_NOW_spend_analysis" → "Agent running : Spend Analysis"
// "spend_analysis"             → "Agent: Spend Analysis"
function formatAgentStatus(val) {
  if (!val || typeof val !== "string") return null;
  const name = resolveToolName(val);
  return /^RUNNING_NOW_/i.test(val)
    ? `Agent running : ${name}`
    : ` Response generated from ${name}`;
}



function parseMaybeJSON(raw) {
  // NEW: accept non-string input directly
  if (raw && typeof raw === "object") return raw;

  if (!raw || typeof raw !== "string") return null;

  // Try strict JSON first
  try {
    return JSON.parse(raw);
  } catch (_) {}

  // Try a stringified JSON (e.g., "\"{...}\"")
  try {
    const unwrapped = raw.trim();
    if (
      (unwrapped.startsWith('"') && unwrapped.endsWith('"')) ||
      (unwrapped.startsWith("'") && unwrapped.endsWith("'"))
    ) {
      const unq = unwrapped.slice(1, -1);
      return JSON.parse(unq);
    }
  } catch (_) {}

  // Not JSON
  return null;
}

// Helper: robustly pull a string field from any input (object/array/preview string)
function extractFieldString(raw, key) {
  // Object/Array cases
  if (Array.isArray(raw)) {
    const first = raw.find((x) => x && typeof x === "object" && key in x) ?? raw[0];
    if (first && typeof first[key] === "string") return first[key];
  } else if (raw && typeof raw === "object") {
    if (typeof raw[key] === "string") return raw[key];
  }

  // String (including DevTools preview)
  if (typeof raw === "string") {
    // Match quoted OR unquoted keys, allow multiline values
    const re = new RegExp(`["']?${key}["']?\\s*:\\s*("([\\s\\S]*?)")`, "i");
    const m = raw.match(re);
    if (m) {
      // Try to JSON.parse the captured quoted string (handles \n, escapes)
      try {
        return JSON.parse(m[1]);
      } catch {
        // Fallback: unescape a little and return inner content
        return m[2].replace(/\\"/g, '"');
      }
    }
  }
  return null;
}

function extractHumanReadable(raw) {
  const out = [];

  // Do NOT hide Plan summary
  const HIDE_LABELS = new Set([]);

  const pushIf = (label, value) => {
    if (!label || HIDE_LABELS.has(label)) return;
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    out.push({ label, value });
  };

// split "1. ... 2. ... 3. ..." into clean items
const bulletize = (text) => {
  const s = String(text || "").trim();
  if (!s) return [];
  // single-line: "1. foo 2. bar 3. baz"
  const parts = s
    .split(/\s(?=\d+\.\s)/g)     // split just before "2. ", "3. ", ...
    .map(t => t.trim())
    .filter(Boolean);
  // if we only got one chunk, leave as-is (no numbering detected)
  if (parts.length <= 1) return [s];
  // strip leading "N. " from each chunk
  return parts.map(t => t.replace(/^\d+\.\s/, "").trim());
};

  

  // 1) Try JSON/object first
  const parsed = parseMaybeJSON(raw);
  if (parsed && typeof parsed === "object") {
    let root = parsed;
    if (Array.isArray(parsed)) {
      root =
        parsed.find((el) => el && typeof el === "object" && ("data" in el || "plan_summary" in el)) ??
        parsed[0];
    }
    const dataObj = root && typeof root === "object" ? root : {};

    // Tool status
// Tool status → always show, nicely formatted
const tool = dataObj.tool || dataObj?.data?.tool;
if (typeof tool === "string") {
  const msg = formatAgentStatus(tool);
  if (msg) out.push({ label: "", value: msg });
}

// also surface plain 'tool' if present in raw text (quoted or unquoted)
const toolStr = extractFieldString(raw, "tool");
if (toolStr) {
  const msg = formatAgentStatus(toolStr);
  if (msg) out.push({ label: "", value: msg });
}


    // Plan summary + Summary
    if (typeof dataObj.plan_summary === "string") pushIf("Plan summary", dataObj.plan_summary);
    if (typeof dataObj.summary === "string") pushIf("Summary", dataObj.summary);

    // Table preview
    if (Array.isArray(dataObj.actual_table_result) && dataObj.actual_table_result.length > 0) {
      pushIf("Preview", { __table__: dataObj.actual_table_result });
    } else if (typeof dataObj.actual_table_result === "string") {
      const t = tryExtractCompactTableFromRaw(dataObj.actual_table_result);
      if (t) pushIf("Preview", { __table__: t });
    }

    // ONLY show `data` (if string)
    if (typeof dataObj.data === "string") {
      const items = bulletize(dataObj.data);
      out.push({ label: "", value: items.length ? { __list__: items, ordered: true } : dataObj.data });
    }

    if (out.length) return out;
  }

  // 2) Fallback for DevTools preview strings (not JSON)
  const planSummary = extractFieldString(raw, "plan_summary");
  if (planSummary) pushIf("Plan summary", planSummary);

  const summary = extractFieldString(raw, "summary");
  if (summary) pushIf("Summary", summary);

  const tableGuess = tryExtractCompactTableFromRaw(raw);
  if (tableGuess) pushIf("Preview", { __table__: tableGuess });

  const dataStr = extractFieldString(raw, "data");
  if (dataStr) {
    const items = bulletize(dataStr);
    out.push({ label: "", value: items.length ? { __list__: items, ordered: true } : dataStr });
  }

  return out;
}


function tryExtractCompactTableFromRaw(raw) {
  // Heuristic table for entries like:
  // {"Vendor Name": "...", "Plant": "...", "Client Spend (USD)": 123}
  const rows = [];
  const itemRegex =
    /{[^}]*"Vendor Name"\s*:\s*"([^"]+)"[^}]*"Plant"\s*:\s*"([^"]+)"[^}]*"Client Spend \(USD\)"\s*:\s*([0-9.]+)/g;
  let m;
  while ((m = itemRegex.exec(raw))) {
    rows.push({
      "Vendor Name": m[1],
      Plant: m[2],
      "Client Spend (USD)": Number(m[3]),
    });
    if (rows.length >= 10) break; // keep compact
  }
  return rows.length ? rows : null;
}

function HumanBlock({ sections }) {
  return (
    <div style={{ display: "grid", gap: 8, marginLeft: 20 }}>
      {sections.map((s, i) => {
        const v = s.value;
        const showHeader =
          typeof s.label === "string" && s.label.trim().length > 0;

        // Table case unchanged ...
        if (
          v &&
          typeof v === "object" &&
          v.__table__ &&
          Array.isArray(v.__table__) &&
          v.__table__.length
        ) {
          // ... same as before
        }

        // Structured list (used for Details)
        if (
          v &&
          typeof v === "object" &&
          v.__list__ &&
          Array.isArray(v.__list__)
        ) {
          const ListTag = v.ordered ? "ol" : "ul";
          return (
            <div key={i} style={{ display: "grid", gap: 4 }}>
              {showHeader && (
                <div style={{ fontSize: 12, fontWeight: 400, color: "#FFFFFF" }}>
                  {s.label}
                </div>
              )}
              <ListTag style={{ margin: 0, padding: 0, listStylePosition: "inside" }}>
                {v.__list__.map((line, li) => (
                  <li
                    key={li}
                    style={{
                      lineHeight: 1.45,
                      color: "#FFFFFFB2",
                      fontSize: 12,
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ListTag>
            </div>
          );
        }

        // Legacy array → keep as unordered, remove extra padding
        if (Array.isArray(v)) {
          return (
            <div key={i} style={{ display: "grid", gap: 4 }}>
              {showHeader && (
                <div style={{ fontSize: 12, fontWeight: 400, color: "#FFFFFF" }}>
                  {s.label}
                </div>
              )}
              <ul style={{ margin: 0, padding: 0, listStylePosition: "inside" }}>
                {v.map((line, li) => (
                  <li
                    key={li}
                    style={{
                      lineHeight: 1.45,
                      color: "#FFFFFFB2",
                      fontSize: 12,
                    }}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        // Simple text ...
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          return (
            <div key={i}>
              {showHeader && (
                <div style={{ fontSize: 12, fontWeight: 400, color: "#FFFFFF" }}>
                  {s.label}
                </div>
              )}
              <div style={{ color: "#FFFFFFB2", fontSize: 12, lineHeight: 1.45 }}>
                {String(v)}
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}


function splitLog(text) {
  const line = text || "";

  // Hide internal tags like [planner #1] — users don't need to see these.
  // We still detect sections like "🧭 Planner: ..." or "🛠️ Tools: ..."
  const headerMatch = line.match(
    /^(?<icon>[^\w\s])\s*(?<header>(Planner|Tools|Orchestrator|Executor|Final|Agent|Narrative|Answer|Logs|Status).*?)$/i
  );
  if (headerMatch?.groups) {
    return {
      icon: headerMatch.groups.icon,
      header: headerMatch.groups.header,
      payload: "",
    };
  }

  // If a line has a bullet with dict/array, suppress the bracketed tag from header
  const bulletDict = line.match(/^\s*•\s*\[[^\]]+\]\s*(?<rest>[\s\S]+)$/);
  if (bulletDict?.groups) {
    return { payload: bulletDict.groups.rest };
  }

  // Generic "key: value" without dicts
  const colonMatch = line.match(
    /^(?<icon>[^\w\s])?\s*(?<key>[^:]+):\s*(?<val>.+)$/
  );
  if (colonMatch?.groups && !line.includes("{") && !line.includes("[")) {
    const key = colonMatch.groups.key.trim();
    const val = colonMatch.groups.val.trim();
  
    // If it’s a Tool header, convert to the desired Agent text
    if (/^tool\b/i.test(key)) {
      const msg = formatAgentStatus(val);
      if (msg) {
        return { icon: colonMatch.groups.icon || "", header: msg, payload: "" };
      }
    }
  
    // Otherwise, keep the generic "Key: Val"
    return {
      icon: colonMatch.groups.icon || "",
      header: `${key}: ${val}`,
      payload: "",
    };
  }
  

  // Fallback: dict/array as payload
  if (line.includes("{") || line.includes("[")) return { payload: line };

  return { icon: "", header: line.trim(), payload: "" };
}

export const LogItem = ({ text, accentColor = "#9ca3af" }) => {
  const { icon, header, payload } = splitLog(text);
  const sections = payload ? extractHumanReadable(payload) : [];
  const hasHuman = sections.length > 0;

  const isStandaloneDetailsHeader =
    header && !payload && header.trim().toLowerCase() === "details";
  if (isStandaloneDetailsHeader) return null;

  const isAgentRunningHeader =
    header && !payload && /^agent running\b/i.test(header);
  // if (isAgentRunningHeader && !/RUNNING_NOW_/i.test(header)) return null;

  const anySectionHasLabel = sections.some(
    (s) => typeof s.label === "string" && s.label.trim().length > 0
  );
  const showHeaderOnly = header && !payload;

  // 🔧 helper: flatten unlabeled sections into a single inline string
  const inlineFromSections = (secs) => {
    const parts = [];
    for (const s of secs) {
      const v = s?.value;
      if (v == null) continue;
      if (typeof v === "string") {
        parts.push(v.trim());
      } else if (Array.isArray(v)) {
        parts.push(v.join(" • "));
      } else if (typeof v === "object") {
        if (v.__list__ && Array.isArray(v.__list__))
          parts.push(v.__list__.join(" • "));
        else if (v.__table__ && Array.isArray(v.__table__))
          parts.push(`Preview (${v.__table__.length} rows)`);
      }
    }
    return parts.filter(Boolean).join(" • ");
  };

  if (hasHuman) {
    const allUnlabeled = sections.every((s) => !s.label || !s.label.trim());
    const inlineText = allUnlabeled ? inlineFromSections(sections) : "";

    return (
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        {icon ? (
          <span style={{ color: accentColor, fontSize: 14, lineHeight: 1 }}>
            {icon}
          </span>
        ) : null}

        <div style={{ display: "grid", gap: 6, flex: 1 }}>
          {/* If there are labeled sections, show the header above them */}
          {!showHeaderOnly &&
          header &&
          payload &&
          !allUnlabeled &&
          anySectionHasLabel ? (
            <div style={{ fontSize: 13, fontWeight: 400, color: "#e5e7eb" }}>
              {header}
            </div>
          ) : null}

          {allUnlabeled && inlineText ? (
            // ✅ inline text next to the icon (no extra margins)
            <div style={{ color: "#9ca3af", fontSize: 12.5, lineHeight: 1.45 }}>
              {inlineText}
            </div>
          ) : (
            // rich, labeled content stays as your table/list renderer
            <HumanBlock sections={sections} />
          )}
        </div>
      </div>
    );
  }

  // No payload → simple header row
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {icon ? (
        <span style={{ color: accentColor, fontSize: 14, lineHeight: 1 }}>
          {icon}
        </span>
      ) : null}
      {header ? (
        <span
          style={{
            fontSize: 12,
            fontWeight: 400,
            color: "#FFFFFFB2",
            whiteSpace: "pre-line",
          }}
        >
          {header}
        </span>
      ) : null}
    </div>
  );
};
